import React, { useState, useContext } from 'react';
import { useForm } from '@abyss/web/hooks/useForm';
import { styled } from '@abyss/web/tools/styled';
import { Button } from '@abyss/web/ui/Button';
import { FormProvider } from '@abyss/web/ui/FormProvider';
import { PropsContext } from 'src/hooks/usePropsContext/PropsContext';
import { useAdminContext } from 'src/hooks/useAdminContext';
import { useUserInfoStore } from 'src/store/useUserInfoStore';
import { config } from '@abyss/ui/tools/config';
import { AlertContext } from 'src/tools/AlertContext';
import {
  usePesLocationSearch,
  parseResult,
} from 'src/hooks/usePesLocationSearch';
import { ApiErrorModal } from '../components/ApiErrorModal';
import { SearchResults } from '../components/SearchResults';
import { NoMatchesFound } from '../components/NoMatchesFound';
import { TaxID } from '../components/TaxID';

const FormButtonContainer = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '10px',
});

const StyledSearchResultsContainer = styled('div', {
  marginTop: 25,
});

const StyledContinueContainer = styled('div', {
  marginTop: 25,
});

const StyledButton = styled(Button, {
  '&.abyss-button-root': {
    marginTop: '20px',
  },
});

const StyledFormProvider = styled(FormProvider, {
  marginTop: '16px',
});

export const SearchForm = () => {
  const form = useForm({
    defaultValues: {
      taxId: '',
    },
  });

  const [isApiErrorModalOpen, setIsApiErrorModalOpen] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [showNoMatchesFound, setShowNoMatchesFound] = useState(false);
  const [disableContinueButton, setDisableContinueButton] = useState(true);
  const [selectedSearchData, setSelectedSearchData] = useState({});
  const [taxIdValue, setTaxIdValue] = useState('');
  const { data: userInfo } = useUserInfoStore();
  const errorContext = useContext(AlertContext);

  const [, getPesLocations] = usePesLocationSearch({
    onCompleted: response => {
      if (response.providerData.length === 0) {
        setShowSearchResults(false);
        setShowNoMatchesFound(true);
      } else {
        setShowSearchResults(true);
        setShowNoMatchesFound(false);
      }
    },
    onError: error => {
      setShowSearchResults(false);
      if (error) {
        setIsApiErrorModalOpen(true);
      } else {
        setShowNoMatchesFound(true);
      }
    },
  });

  const [, setAdminContext] = useAdminContext({
    onCompleted: () => {
      window.location.replace(config('MPP_URL'));
    },
    onError: error => {
      errorContext.addAlert({
        ...error,
        message:
          'The system is unable to respond at the moment. Please try refreshing the page or try again at a later time',
      });
    },
  });

  const getPageData = async (page = 0, pageSize = 10) => {
    let startValue = 0;

    if (page > 0) {
      startValue = page * pageSize;
    }

    const result = await getPesLocations({
      taxIdNbr: taxIdValue,
      start: startValue,
      count: pageSize,
    });
    const data = result.body.data ? result.body.data : result.body;
    const parsedResult = parseResult(data);

    return {
      results: parsedResult.providerData,
      count: parsedResult.total,
    };
  };

  const handleReset = () => {
    form.reset();
    setDisableContinueButton(true);
    setShowNoMatchesFound(false);
    setShowSearchResults(false);
  };

  const handleSearch = data => {
    setShowNoMatchesFound(false);
    setTaxIdValue(data.taxId);
    setShowSearchResults(true);
  };

  const closeModal = () => {
    setIsApiErrorModalOpen(false);
  };

  const handleContinue = () => {
    const provider = {
      servicingProviderName: selectedSearchData.servicingProviderName,
      servicingProviderFirstName: selectedSearchData.servicingProviderFirstName,
      servicingProviderLastName: selectedSearchData.servicingProviderLastName,
      servicingProviderMpin: selectedSearchData.servicingProviderMpin,
      corporateProviderMpin: selectedSearchData.corporateProviderMpin,
      corporateProviderLastName: selectedSearchData.corporateProviderLastName,
      corporateProviderName: selectedSearchData.corporateProviderName,
      npi: selectedSearchData.npi,
      taxIdNumber: selectedSearchData.taxIdNumber,
    };

    setAdminContext({
      variables: {
        role: 'pace-admin',
        id: userInfo?.uuid,
        username: userInfo?.username,
        provider,
      },
    });
  };

  const handleOnBlur = field => {
    form.trigger(`${field}`);
  };

  const widgetProps = {
    onBlur: handleOnBlur,
  };

  return (
    <React.Fragment>
      <ApiErrorModal isOpen={isApiErrorModalOpen} onClose={closeModal} />
      <StyledFormProvider state={form} onSubmit={handleSearch} highlighted>
        <PropsContext.Provider value={widgetProps}>
          <TaxID />
        </PropsContext.Provider>
        <FormButtonContainer>
          <StyledButton type="submit" data-testid="search-button">
            Search
          </StyledButton>
          <StyledButton
            type="reset"
            variant="outline"
            onClick={handleReset}
            data-testid="reset-button"
          >
            Reset
          </StyledButton>
        </FormButtonContainer>
      </StyledFormProvider>
      <If condition={showSearchResults}>
        <StyledSearchResultsContainer>
          <SearchResults
            setDisableContinueButton={setDisableContinueButton}
            setSelectedSearchData={setSelectedSearchData}
            taxIdValue={taxIdValue || ''}
            getPageData={getPageData}
          />
        </StyledSearchResultsContainer>
      </If>
      <If condition={showNoMatchesFound}>
        <StyledSearchResultsContainer>
          <NoMatchesFound taxIdValue={taxIdValue || ''} />
        </StyledSearchResultsContainer>
      </If>
      <If condition={showSearchResults}>
        <StyledContinueContainer>
          <Button isDisabled={disableContinueButton} onClick={handleContinue}>
            Continue
          </Button>
        </StyledContinueContainer>
      </If>
    </React.Fragment>
  );
};
